import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnInit,
  inject
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { of } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';

import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';

import DOMPurify from 'dompurify';

import { OutlookDataService } from '../../services/outlook-data.service';
import { OutlookPanelService } from '../../services/outlook-panel.service';
import {
  IOutlookMessageDetail,
  IOutlookMessageListItem,
  OutlookWellKnownFolder
} from '../../models/IOutlookMessage.interface';

interface FolderPill {
  key: OutlookWellKnownFolder;
  label: string;
  icon: string;
}

/**
 * Mail list plus reading pane.
 *
 * Read-only throughout. Nothing here marks a message as read, moves it, or deletes it — those all
 * need Mail.ReadWrite, which the integration deliberately does not hold. Replying and forwarding
 * hand off to Outlook Web, and the footer says so rather than leaving the absence unexplained.
 */
@Component({
  selector: 'app-outlook-mail-tab',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatProgressSpinnerModule,
    MatTooltipModule
  ],
  templateUrl: './outlook-mail-tab.component.html',
  styleUrls: ['./outlook-mail-tab.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OutlookMailTabComponent implements OnInit {
  private readonly data = inject(OutlookDataService);
  private readonly panel = inject(OutlookPanelService);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  readonly folders: FolderPill[] = [
    { key: 'inbox', label: 'Inbox', icon: 'inbox' },
    { key: 'sentitems', label: 'Sent', icon: 'send' },
    { key: 'drafts', label: 'Drafts', icon: 'drafts' },
    { key: 'archive', label: 'Archive', icon: 'archive' }
  ];

  activeFolder: OutlookWellKnownFolder = 'inbox';
  messages: IOutlookMessageListItem[] = [];
  loading = false;

  selected: IOutlookMessageDetail | null = null;
  loadingDetail = false;
  /** Sanitised body, ready for the reading pane's sandboxed iframe. */
  safeBody: SafeHtml | null = null;

  readonly searchControl = new FormControl<string>('', { nonNullable: true });
  searching = false;

  ngOnInit(): void {
    this.loadFolder(this.activeFolder);

    this.searchControl.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((term) => {
          const query = (term || '').trim();
          if (!query) {
            this.searching = false;
            return this.data.getMessages(this.activeFolder);
          }
          this.searching = true;
          this.loading = true;
          this.cdr.markForCheck();
          return this.data.searchMessages(query);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((messages) => {
        this.messages = messages;
        this.loading = false;
        this.cdr.markForCheck();
      });

    // The shell owns the selection so it can widen the panel; this tab follows it.
    this.panel.selectedMessageId$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((id) => {
      if (!id) {
        this.selected = null;
        this.safeBody = null;
        this.cdr.markForCheck();
        return;
      }
      this.loadDetail(id);
    });
  }

  selectFolder(folder: OutlookWellKnownFolder): void {
    if (folder === this.activeFolder) {
      return;
    }
    this.activeFolder = folder;
    this.panel.selectMessage(null);
    if (this.searchControl.value) {
      // Clearing the box re-runs the folder load through the valueChanges pipeline.
      this.searchControl.setValue('');
      return;
    }
    this.loadFolder(folder);
  }

  refresh(): void {
    this.loadFolder(this.activeFolder, true);
  }

  openMessage(message: IOutlookMessageListItem): void {
    this.panel.selectMessage(message.id);
  }

  closeReadingPane(): void {
    this.panel.selectMessage(null);
  }

  clearSearch(): void {
    this.searchControl.setValue('');
  }

  // ─── Outlook Web hand-offs ───

  openInOutlook(id: string): void {
    window.open(this.data.owaMessageUrl(id), '_blank', 'noopener');
  }

  replyInOutlook(message: IOutlookMessageDetail): void {
    window.open(
      this.data.owaComposeUrl(message.fromAddress, `Re: ${message.subject}`),
      '_blank',
      'noopener'
    );
  }

  forwardInOutlook(message: IOutlookMessageDetail): void {
    window.open(this.data.owaComposeUrl(undefined, `Fw: ${message.subject}`), '_blank', 'noopener');
  }

  trackById(_index: number, item: IOutlookMessageListItem): string {
    return item.id;
  }

  // ─── Internals ───

  private loadFolder(folder: OutlookWellKnownFolder, force = false): void {
    this.loading = true;
    this.cdr.markForCheck();

    this.data
      .getMessages(folder, force)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((messages) => {
        this.messages = messages;
        this.loading = false;
        this.cdr.markForCheck();
      });
  }

  private loadDetail(id: string): void {
    this.loadingDetail = true;
    this.safeBody = null;
    this.cdr.markForCheck();

    this.data
      .getMessage(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((detail) => {
        this.selected = detail;
        this.safeBody = detail ? this.renderBody(detail) : null;
        this.loadingDetail = false;
        this.cdr.markForCheck();
      });
  }

  /**
   * Prepares a message body for the reading pane.
   *
   * Two separate problems are being solved, and both need the iframe:
   *
   * 1. Security. The body is attacker-controlled HTML from an arbitrary sender. DOMPurify strips
   *    scripts and event handlers; the iframe's empty sandbox attribute (no allow-scripts, no
   *    allow-same-origin) means even a sanitiser bypass has no capability to use.
   * 2. Layout. Outlook bodies routinely carry <style> blocks with bare body{} and table{} rules.
   *    Injected with [innerHTML] those escape into the panel and wreck its chrome. An iframe is the
   *    only clean containment for that.
   *
   * bypassSecurityTrustHtml is safe here specifically because it is applied to DOMPurify output,
   * and because srcdoc content lands in a sandboxed document rather than this origin.
   */
  private renderBody(detail: IOutlookMessageDetail): SafeHtml {
    if (detail.bodyType === 'text') {
      const escaped = detail.bodyHtml
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
      return this.sanitizer.bypassSecurityTrustHtml(
        `<pre style="white-space:pre-wrap;font-family:Lato,Segoe UI,sans-serif;font-size:13px;">${escaped}</pre>`
      );
    }

    const clean = DOMPurify.sanitize(detail.bodyHtml, {
      WHOLE_DOCUMENT: true,
      FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form'],
      FORBID_ATTR: ['srcset', 'ping']
    });

    // Remote images are left in place but the iframe sends no referrer, so opening a message does
    // not leak the reader's context back to a tracking pixel's host.
    const shell =
      `<base target="_blank">` +
      `<style>body{margin:0;padding:12px;font-family:Lato,Segoe UI,Arial,sans-serif;font-size:13px;color:#1f2933;}` +
      `img{max-width:100%;height:auto;}table{max-width:100%;}</style>` +
      clean;

    return this.sanitizer.bypassSecurityTrustHtml(shell);
  }
}
